import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RegistrationForm from './components/RegistrationForm';
import LoginForm from './components/LoginForm';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import MBCBReports from './pages/MBCBReports';
import MBCBReportEditor from './pages/MBCBReportEditor';
import Profile from './pages/Profile';
import ChurchProfile from './pages/ChurchProfile';
import RequirePortalCandidate from './components/RequirePortalCandidate';
import OnboardingWizard from './components/OnboardingWizard';
import AssessmentSchedule from './pages/AssessmentSchedule';
import AssessmentReport from './pages/AssessmentReport';
import Training from './pages/Training';
import PlanterInvite from './pages/PlanterInvite';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route 
                path="/register" 
                element={
                  <ProtectedRoute requiresAuth={false}>
                    <RegistrationForm />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/login" 
                element={
                  <ProtectedRoute requiresAuth={false}>
                    <LoginForm />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/forgot-password" 
                element={
                  <ProtectedRoute requiresAuth={false}>
                    <ForgotPassword />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/reset-password" 
                element={
                  <ProtectedRoute requiresAuth={false}>
                    <ResetPassword />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/verify-email"
                element={<VerifyEmail />}
              />
              <Route
                path="/planter-invite"
                element={<PlanterInvite />}
              />
              
              {/* Protected routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/mbcb" 
                element={
                  <ProtectedRoute>
                    <MBCBReports />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/mbcb/report/:reportId"
                element={
                  <ProtectedRoute>
                    <MBCBReportEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training"
                element={
                  <ProtectedRoute>
                    <Training />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/church-profile"
                element={
                  <ProtectedRoute>
                    <ChurchProfile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assessment-schedule"
                element={
                  <ProtectedRoute>
                    <RequirePortalCandidate>
                      <AssessmentSchedule />
                    </RequirePortalCandidate>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/assessment-report"
                element={
                  <ProtectedRoute>
                    <RequirePortalCandidate>
                      <AssessmentReport />
                    </RequirePortalCandidate>
                  </ProtectedRoute>
                }
              />
              
              {/* Onboarding wizard */}
              <Route 
                path="/onboarding" 
                element={
                  <ProtectedRoute>
                    <RequirePortalCandidate>
                      <OnboardingWizard />
                    </RequirePortalCandidate>
                  </ProtectedRoute>
                } 
              />
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/register" replace />} />
              
              {/* Catch all route */}
              <Route 
                path="*" 
                element={
                  <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        Page Not Found
                      </h2>
                      <p className="text-gray-600 mb-4">
                        The page you're looking for doesn't exist.
                      </p>
                      <button
                        onClick={() => window.location.href = '/'}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Go Home
                      </button>
                    </div>
                  </div>
                } 
              />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
